import { Component, EventEmitter, forwardRef, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import 'brace';
import 'brace/theme/clouds';
import 'brace/mode/xml';
import 'brace/mode/sql';
import 'brace/ext/language_tools.js';
import {AceConfig} from 'ngx-ace-wrapper';

@Component({
  selector: 'tcc-code-editor',
  template: `
    <div class="editor-wrapper" [class.fullscreen]="fullscreen">
      <button mat-icon-button class="toggle-fullscreen" (click)="toggleFullscreen()">
        <mat-icon aria-label="Toggle fullscreen">{{ fullscreen ? 'fullscreen_exit' : 'fullscreen' }}</mat-icon>
      </button>
      <span class="code-editor-label" [class.label-invalid]="(isInvalid && !value)">{{ label }}</span>
      <ace [mode]="mode"
           [theme]="theme"
           [disabled]="readOnly"
           [config]="options"
           [(value)]="value"
           [class.isInvalid]="(isInvalid && !value)"
      ></ace>
    </div>
  `,
  styleUrls: ['./code-editor.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => CodeEditorComponent),
    }
  ]
})
export class CodeEditorComponent implements OnInit, ControlValueAccessor {

  readonly options = {
    highlightActiveLine: true,
    highlightSelectedWord: true,
    autoScrollEditorIntoView: true,
    highlightGutterLine: true,
    animatedScroll: true,
    enableBasicAutocompletion: true,
    enableLiveAutocompletion: true,
  };


  @Input() mode = 'sql';
  @Input() theme = 'clouds';
  @Input() readOnly = false;
  @Input() autoUpdateContent = true;
  @Input() durationBeforeCallback = 1000;
  @Input() label: string;
  @Input() fullscreen = false;
  @Input() isInvalid = false;

  /**
   * Gets called when the editor's value is about to change
   * @type {EventEmitter<string>} The editor's new value
   */
  @Output() textChange = new EventEmitter<string>();

  /**
   * Gets called when the editor's value has changed
   * @type {EventEmitter<string>} The editor's new value
   */
  @Output() textChanged = new EventEmitter<string>();

  _value: string;

  _changeFn: (_: any) => void;
  _touchedFn: any;


  constructor() {
  }


  ngOnInit() {

  }


  // ControlValueAccessor implementation

  registerOnChange(fn: (_: any) => void): void {
    this._changeFn = fn;
  }

  registerOnTouched(fn: any): void {
    this._touchedFn = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.readOnly = isDisabled;
  }

  writeValue(obj: any): void {
    this.value = obj;
  }


  // Event handling

  onTextChange(text: string) {
    this._value = text;

    if (this._changeFn) {
      this._changeFn(text);
    }

    this.textChange.emit(text);
  }

  onTextChanged(text: string) {
    this._value = text;

    if (this._changeFn) {
      this._changeFn(text);
    }

    this.textChanged.emit(text);
  }


  // Property accessors

  get value(): string {
    return this._value;
  }

  set value(value: string) {
    this.onTextChange(value);
    this._value = value;
    this.onTextChanged(value);
  }


  toggleFullscreen() {
    this.fullscreen = !this.fullscreen;
  }

}
